import type { AssistantEntry, ProjectMeta } from './types';

/**
 * Which assistants have worked on this board, and what each of them may do.
 *
 * TWO FACTS, AND BOTH HAVE TO BE TRUE
 *
 *   it is on the list  — NOT something a person adds. A tool records itself the
 *                        first time it touches the board. A box claiming an
 *                        assistant is present proves nothing; one actually
 *                        writing here is the fact, and nothing can be permitted
 *                        that has never been seen.
 *   it is switched on  — the person's decision, per assistant, off until they
 *                        say otherwise and theirs to withdraw at any time.
 *
 * PER ASSISTANT, NOT PER BOARD
 *
 * "An AI may read the comments" stops being one decision the moment two of them
 * are working on the same project. One switch would mean permitting a new tool
 * nobody has looked at in order to keep the one already trusted, which is how a
 * permission quietly becomes meaningless.
 *
 * WHAT THIS PERMISSION IS NOT
 *
 * It does not make the board call anything. This app makes no outbound request
 * beyond its own database, which is what lets it work with no connection, and
 * that is not being traded away. The list is a standing answer that tools read
 * before they act: not on it, or switched off, and the tool refuses.
 */

/** Everything that has worked here, newest first, ready to render. */
export function assistantsOf(meta: Pick<ProjectMeta, 'assistants'>): (AssistantEntry & { id: string })[] {
  return Object.entries(meta.assistants ?? {})
    .map(([id, entry]) => ({ id, ...entry }))
    .sort((a, b) => (b.lastSeen ?? 0) - (a.lastSeen ?? 0));
}

/**
 * HOW FAR THROUGH SETTING ONE UP SOMEBODY IS, AND WHAT THEY DO NEXT.
 *
 * WHY THIS IS A FUNCTION AND NOT A FEW `&&`s ON THE SCREEN.
 *
 * On 17 August Vinnie added an assistant and found the four steps had vanished
 * — "the first button is to add the assistant, there is nothing you can do
 * after that, and that is a big flaw". He was right, and worse than he said:
 * steps 2, 3 and 4 sat inside a block rendered only while
 * `assistants.length === 0 || adding`, so pressing Add made the count 1, set
 * `adding` false, and unmounted all three in the instant they became relevant.
 * The account was never issued and the instructions never shown.
 *
 * Every screen check passed, because a static render of a screen that destroys
 * itself on first click looks perfect. Nothing pressed the button. So the rule
 * about what is still outstanding lives HERE, in something a test can call with
 * an entry and no browser at all — the part that broke is now the part that is
 * checkable.
 *
 * It is also the thing the person reads. The page shows exactly this list, so
 * "what is left to do" and "what the software believes is left to do" cannot
 * drift apart: there is one answer and both of them read it.
 */
export type SetupStep = 'name' | 'pusher' | 'account' | 'instructions';

export interface SetupProgress {
  /** Steps settled, in order. */
  done: SetupStep[];
  /** The next thing to do, or null when nothing is outstanding. */
  next: SetupStep | null;
  /** One line telling the person what to do about `next`. Empty when done. */
  todo: string;
  /** Whether the account step applies at all — 'person' pushing means it does not. */
  needsAccount: boolean;
  finished: boolean;
}

/** What to say about each outstanding step, in the second person. */
const TODO: Record<SetupStep, string> = {
  name: 'Give it a name, so you can tell it apart in the list.',
  pusher: 'Say who puts the tracker on the board — you, or the assistant.',
  account:
    'It needs an account, because it signs in to write here. Nothing is emailed; you are handed the password once.',
  instructions:
    'Copy the instructions and paste them into your assistant. It is the last thing you have to do.',
};

export function setupProgress(
  entry: Pick<AssistantEntry, 'name' | 'email' | 'pusher' | 'lastSeen'>
): SetupProgress {
  const done: SetupStep[] = [];
  if (entry.name?.trim()) done.push('name');
  if (entry.pusher) done.push('pusher');

  /* Only when the assistant does the pushing. Somebody who pushes for it never
     needs to issue credentials, and showing them an unticked box forever would
     be the screen inventing work it has already been told is not required. */
  const needsAccount = entry.pusher === 'assistant';
  if (needsAccount && entry.email) done.push('account');

  /*
   * INSTRUCTIONS COUNT AS DONE WHEN IT HAS ACTUALLY WORKED HERE, never when
   * the Copy button was pressed. Copying proves the clipboard changed. An
   * assistant appearing in `lastSeen` proves it read the instructions, found
   * the board and wrote to it — which is the thing being set up, and the only
   * evidence of it that cannot be faked by a stray press.
   */
  if (entry.lastSeen) done.push('instructions');

  const order: SetupStep[] = needsAccount
    ? ['name', 'pusher', 'account', 'instructions']
    : ['name', 'pusher', 'instructions'];
  const next = order.find((s) => !done.includes(s)) ?? null;

  return {
    done,
    next,
    todo: next ? TODO[next] : '',
    needsAccount,
    finished: next === null,
  };
}

/** Whether anything has ever worked here — what decides if the list is shown. */
export function anyAssistants(meta: Pick<ProjectMeta, 'assistants' | 'keptByAssistant'>): boolean {
  return Object.keys(meta.assistants ?? {}).length > 0 || meta.keptByAssistant === true;
}

/** Whether this particular assistant may read and answer comments here. */
export function mayAnswerComments(
  meta: Pick<ProjectMeta, 'assistants'>,
  who: string
): boolean {
  return meta.assistants?.[who]?.mayAnswer === true;
}

/** What to call one on screen. The key is a fallback, never a blank. */
export function assistantName(entry: Pick<AssistantEntry, 'name'>, id: string): string {
  return entry.name?.trim() || id;
}

/**
 * Why a tool is refusing, in words the person reading a terminal can act on.
 * Returns null when it may proceed.
 */
export function whyNotAllowed(meta: Pick<ProjectMeta, 'assistants'>, who: string): string | null {
  const entry = meta.assistants?.[who];
  if (!entry) {
    return (
      `  "${who}" has not been recorded on this board yet, so there is nothing to\n` +
      `  permit. It is added the first time it does anything here, and the switch\n` +
      `  appears in Settings only then.\n`
    );
  }
  if (entry.mayAnswer !== true) {
    return (
      `  "${assistantName(entry, who)}" is not permitted to read comments on this board.\n` +
      `  Turn it on in Settings → Comments.\n`
    );
  }
  return null;
}

/**
 * An assistant can be given work, like anybody else.
 *
 * Assignees are stored as a plain string — an email address for a person. An
 * assistant has no address, so its own identifier is stored instead, and both
 * are read back through here. Anything not recognised as an assistant is a
 * person, which is what keeps every assignee written before this still working.
 *
 * Prefixed on the way in so the two can never collide: somebody whose address
 * happened to match an assistant's name would otherwise be silently swapped for
 * a machine.
 */
const ASSISTANT_PREFIX = 'assistant:';

export function assistantAssignee(id: string): string {
  return `${ASSISTANT_PREFIX}${id}`;
}

/** The assistant an assignee refers to, or null if it refers to a person. */
export function assigneeAssistant(
  assignee: string,
  meta: Pick<ProjectMeta, 'assistants'>
): (AssistantEntry & { id: string }) | null {
  if (!assignee.startsWith(ASSISTANT_PREFIX)) return null;
  const id = assignee.slice(ASSISTANT_PREFIX.length);
  const entry = meta.assistants?.[id];
  // Named but no longer registered — still an assistant, and saying so beats
  // showing a raw "assistant:whatever" or pretending it is a person.
  return { id, ...(entry ?? {}) };
}

/** What to call whoever an issue is assigned to, person or assistant. */
export function assigneeName(
  assignee: string,
  meta: Pick<ProjectMeta, 'assistants'>,
  nameOfPerson: (email: string) => string
): string {
  const bot = assigneeAssistant(assignee, meta);
  return bot ? bot.name?.trim() || bot.id : nameOfPerson(assignee);
}

/**
 * Who a new issue should be assigned to before anybody chooses.
 *
 * On a board an assistant runs, the assistant is doing the work — so leaving
 * every new issue unassigned means the answer to "who has this" is blank on a
 * board where it is never in doubt.
 *
 * Only when exactly ONE is registered. With two there is no "the assistant" to
 * fill in, and picking the first would be a guess presented as a fact — the same
 * mistake as reading a blocker's kind out of its wording. Blank is honest.
 */
export function defaultAssignee(meta: Pick<ProjectMeta, 'assistants'>): string {
  const all = assistantsOf(meta);
  return all.length === 1 ? assistantAssignee(all[0].id) : '';
}

/**
 * WHO A NEW CARD STARTS ASSIGNED TO, before anybody chooses — the single default
 * the three create paths (quick-add, the card form, add-from-plan) all share, so
 * they cannot drift.
 *
 * An assistant-run board still wins first (`defaultAssignee`): the assistant is
 * doing the work there. On every other board the default is now the PERSON
 * CREATING THE CARD — whoever makes a card owns it until they hand it on, so work
 * is never born unassigned.
 *
 * This replaces the old sole-member fallback (`soleMember`), which only filled in
 * an assignee when a board had EXACTLY one member. A workspace admin can open any
 * board, and is offered as an assignee on it, so almost no real board was ever
 * truly single-member — the heuristic fell through to blank and new cards landed
 * unowned, including the ones their creator had just made. Vinnie, 6 Oct 2026.
 *
 * The creator is written as their canonical EMAIL, lower-cased to match how an
 * assignee picked from the board reads back — never a display name, which is a
 * separate identity-form question handled elsewhere. An empty `meEmail` (nobody
 * signed in, which should not happen on a board) falls through to blank, exactly
 * as an assistant-less board with no creator would.
 */
export function defaultCardAssignee(meta: Pick<ProjectMeta, 'assistants'>, meEmail: string): string {
  return defaultAssignee(meta) || meEmail.trim().toLowerCase();
}
